import { useState, useEffect, useContext } from "react";
import Checklist from "components/checklist";
import FormContainer from "components/form-container";
import ProgressBar from "components/progress-bar";
import { useLocation, useOutletContext } from "react-router-dom";
import { getQueryParams } from "lib/utils";
import { useRestartIfAbsent } from "hooks/useRestartIfAbsent";
import { useTranslation } from "react-i18next";
import { useCurrentLocation } from "lib/utils";
import { TAKEBACK_METHOD_NAME } from "consts/query-params";
import stores from "consts/stores.json";
import AuthContext from "contexts/AuthContext";
import { Link } from "react-router-dom";
import weightRestrictions from "consts/weight-restrictions.json";
import FloodlightEvent from "components/floodlight-event";

export default function ChecklistPage() {
  const [allCheckedList1, setAllCheckedList1] = useState(false);
  const [allCheckedList2, setAllCheckedList2] = useState(false);

  // translation
  const { t } = useTranslation();

  //reset right side visual
  const { setSidebarVisual } = useOutletContext();
  useEffect(() => {
    setSidebarVisual("bricks");
  }, [setSidebarVisual]);

  // Get query params and restart flow if absent
  const location = useLocation();
  const queryParams = getQueryParams(location.search);
  const newQueryParams = {
    ...queryParams,
    p: "giftcard",
  };

  useRestartIfAbsent(
    queryParams,
    ["w", TAKEBACK_METHOD_NAME],
    t("urls.trade_in_weight")
  );

  const [skipMethodPage, setSkipMethodPage] = useState(false);

  const { location: currentLocation } = useCurrentLocation();

  const isKiosk = currentLocation?.takebackMethod
    ? stores.some(
        (store) =>
          currentLocation.takebackMethod.includes(store.value) &&
          store.return_type === "kiosk"
      )
    : false;

  // contitional navigation based on location
  useEffect(() => {
    if (currentLocation) {
      if (
        currentLocation?.takebackMethod?.includes("mail") &&
        currentLocation?.takebackMethod?.length === 1
      ) {
        setSkipMethodPage(true);
      } else {
        setSkipMethodPage(false);
      }
    } else {
      setSkipMethodPage(false);
    }
  }, [queryParams, currentLocation]);

  const backDestination = skipMethodPage
    ? `${t("urls.trade_in_weight")}`
    : `${t("urls.trade_in_method")}`;

  //checklist items
  const checklistItems = [
    {
      name: "brickBasedToys",
      label: t("trade-in.checklist.brick_based_toys"),
    },
    {
      name: "bricksSeparated",
      label: t("trade-in.checklist.bricks_separated"),
    },
    {
      name: "removedElectronics",
      label: t("trade-in.checklist.removed_electronics"),
    },
    {
      name: "removedDuplo",
      label: t("trade-in.checklist.removed_duplo"),
    },
    {
      name: "removedNonLego",
      label: t("trade-in.checklist.removed_non_lego"),
    },
  ];

  const currentStore = currentLocation?.takebackMethod?.find((method) =>
    method.startsWith("in_store")
  )
    ? stores.find(
        (store) =>
          store.value ===
          currentLocation.takebackMethod.find((method) =>
            method.startsWith("in_store")
          )
      )
    : null;

  const maxWeight =
    currentStore && currentStore?.return_type === "kiosk"
      ? weightRestrictions.find((weight) => weight.unit === "lbs").maxKiosk
      : currentStore && currentStore?.return_type === "drop_off"
        ? weightRestrictions.find((weight) => weight.unit === "lbs").maxDropOff
        : weightRestrictions.find((weight) => weight.unit === "lbs").maxMail;

  const checklistItems2 = [
    {
      name: "packingPreparation",
      element: (
        <div>
          I've ensured my bricks are under the {maxWeight.toFixed(2)} lb weight
          limit and have packed them in{" "}
          {queryParams?.[TAKEBACK_METHOD_NAME] === "mail"
            ? "a sturdy cardboard shipping box"
            : isKiosk
              ? "an easy-to-carry container"
              : "a sturdy, unsealed cardboard shipping box"}
          .
        </div>
      ),
    },
  ];

  const { user } = useContext(AuthContext);

  const targetUrl =
    queryParams[TAKEBACK_METHOD_NAME] === "mail"
      ? t("urls.trade_in_donation")
      : !user
        ? t("urls.trade_in_email")
        : isKiosk
          ? t("urls.trade_in_appointment")
          : t("urls.trade_in_summary");

  return (
    <FormContainer
      submitLabel={t("ui.button_continue")}
      submitHref={targetUrl}
      submitParams={newQueryParams}
      submitDisabled={!(allCheckedList1 && allCheckedList2)}
      submitTracking="Trade-in Preparation checklist complete"
      secondaryLabel={t("ui.button_back")}
      secondaryHref={backDestination}
      secondaryParams={queryParams}
    >
      <FloodlightEvent tag="DC-14832541/pageview/pl_check+per_session" />
      <ProgressBar
        step={currentLocation?.state === null ? 2 : 3}
        showLabel
        stepsNum={
          currentLocation?.state === null
            ? user
              ? 5
              : 6
            : queryParams[TAKEBACK_METHOD_NAME] === "mail"
              ? user
                ? 6
                : 7
              : isKiosk
                ? user
                  ? 5
                  : 6
                : user
                  ? 4
                  : 5
        }
      />
      <div className="flex flex-col gap-regular">
        <h2 className="text-primary text-6xl">
          {t("trade-in.checklist.title")}
        </h2>
        <p className="text-tertiary text-2xl">
          Let's ensure they're ready for trade-in.
        </p>
      </div>
      <div className="flex flex-col gap-x-large">
        <div>
          <h3 className="text-2xl font-bold">Preparation Guidelines</h3>

          <Link
            className="text-blue cursor-pointer"
            to={{
              pathname: t("urls.preparation_guidelines"),
              search: `?${new URLSearchParams({ state: queryParams?.state, preselected: queryParams?.[TAKEBACK_METHOD_NAME] === "mail" ? "mail" : "in-store" }).toString()}`,
            }}
            target="_blank"
            rel="noopener noreferrer"
          >
            {queryParams?.[TAKEBACK_METHOD_NAME] === "mail"
              ? "Read our guide on sending your bricks via mail"
              : "Read our full guide on preparing your trade-in"}
            <span className="material-symbols-outlined scale-75 translate-y-[6px] ml-1">
              open_in_new
            </span>
          </Link>
        </div>
        <Checklist
          setAllChecked={setAllCheckedList1}
          checklistItems={checklistItems}
        />
      </div>
      <div className="flex flex-col gap-x-large">
        <h3 className="text-2xl font-bold">Packing Preparation</h3>
        <Checklist
          setAllChecked={setAllCheckedList2}
          checklistItems={checklistItems2}
        />
      </div>
    </FormContainer>
  );
}
